import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactFlow, useNodesState, useEdgesState, Background, useReactFlow, reconnectEdge } from "@xyflow/react";

import "@xyflow/react/dist/style.css";
import "./ActivityRendererNew.css";

import CustomNode from "../../../../components/ReactFlow/ForActivities/CustomNode";
import CircleNode from "../../../../components/ReactFlow/ForActivities/FormNode";
import GroupNode from "../../../../components/ReactFlow/ForActivities/GroupNode";
import BuilderRightSideBar from "../../../../Layouts/BuilderRightSideBar";
import JobConfigration from "../../../../components/BuilderComponents/JobConfigration/JobConfigration";
import EditStateChangeLogic from "./EditStateChangeLogic";
import FunctionNode from "../../../../components/ReactFlow/ForActivities/FunnctionNode";
import { getDefaultNodes, getDefaultParentEdges, jobTypes, getPositionFromConfig, defaultNodePosition } from "./Helper";
import ToolPanel, { REACT_FLOW_TOOL_ACTION } from "../../../../components/ReactFlow/Common/ToolPanel";
import MaterialIcon from "../../../../components/BuilderComponents/MaterialIcon";
import ToggleJobs from "./ToggleJobs";
import Loader from "../../../../Layouts/Loader/Loader";

const templateObject = {};

const nodeTypes = {
  groupNode: GroupNode,
  customNode: (props) => <CustomNode {...props} data={props.data} />,
  functionNode: (props) => <FunctionNode {...props} data={props.data} />,
  circleNode: CircleNode
};

const jobTriggerDataKey = {
  pre: { configKey: "pre_activity_job", jobKey: "pre_activity" },
  pre_submission: { configKey: "pre_submission", jobKey: "pre_submission" },
  post: { configKey: "jobs_trigger", jobKey: "post_activity" }
};

const edgeOptions = [
  { id: "straight", name: "Straight", icon: "trending_flat" },
  { id: "step", name: "Step", icon: "stairs" },
  { id: "bezier", name: "Curve", icon: "gesture" },
  { id: "smoothstep", name: "SmoothStep", icon: "stairs" }
];

const EdgeTypeRadio = ({ selected, onChange }) => {
  return (
    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
      {edgeOptions.map((option) => (
        <label key={option.id} style={{ borderRadius: "4px" }} className="d_flex align_center gap_1 cursor_pointer">
          <input
            type="radio"
            name="edgeType"
            value={option.id}
            checked={selected === option.id}
            onChange={() => onChange(option.id)}
            style={{ marginBottom: "4px" }}
          />
          <MaterialIcon name={option.icon} />
        </label>
      ))}
    </div>
  );
};

const DEFAULT_NODES_VISIBILITY = {
  pre: false,
  pre_submission: false,
  activity: false,
  state: false,
  post: true
};

const ALL_VISIBLE_NODES = {
  pre: true,
  pre_submission: true,
  activity: true,
  state: true,
  post: true
};

//TODO optimize this code
export default function ActivityRendererNew({
  config,
  setJobFlowVisibility,
  settingPreActivityData,
  settingPreSubmission,
  setJobTriggerFormula,
  settingJobTriggerData,
  settingStateChangeLogic,
  settingActivityChangeLogic,
  setPositions,
  logic,
  selectionObj,
  stateid,
  actId,
  allJobs,
  isSmJob,
  currentTab
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState();
  const [edges, setEdges, onEdgesChange] = useEdgesState();
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [selectorPosition, setSelectorPosition] = useState({ x: 0, y: 0, visible: false });
  const [toggleStates, setToggleStates] = useState(
    currentTab === "Job Node" ? ALL_VISIBLE_NODES : config?.positions?.node_states || DEFAULT_NODES_VISIBILITY
  );
  const [isLoading, setIsLoading] = useState(false);

  const [addJob, setAdJob] = useState();
  const [editJob, setEditJob] = useState();
  const [isAutoChildAlignMentRequired] = useState(config?.positions?.nodes);

  const { setViewport, getViewport } = useReactFlow();
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  // Reset viewport after initial render
  useEffect(() => {
    setTimeout(() => {
      setViewport({ x: 0, y: 0, zoom: 0.7 });
    }, 50);

    //eslint-disable-next-line
  }, []);

  //ref is used for getting updated data in ref while timeout calls its callback.
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
  }, [nodes, edges]);

  useEffect(() => {
    setJobFlowVisibility(toggleStates);

    //eslint-disable-next-line
  }, [toggleStates]);

  useEffect(() => {
    if (nodes && edges) {
      const positions = {};

      nodes.forEach((node) => {
        if (node.type === "groupNode") {
          positions[node.data.type] = { ...node.position, ...node.measured };
        } else if (node.type === "circleNode") {
          positions["form"] = node.position;
        } else if (node.type === "functionNode") {
          positions["post_function"] = node.position;
        } else if (node.data.parent === "state") {
          positions[`state_${node.data.index}`] = node.position;
        } else if (node.data.parent === "activity") {
          positions[`activity_${node.data.index}`] = node.position;
        } else {
          positions[`${node.data.parent}_${node.data.index}`] = node.position;
        }
      });

      setPositions({ nodes: positions, edges, node_states: toggleStates });
    }

    //eslint-disable-next-line
  }, [nodes, edges]);

  useEffect(() => {
    let arrangeTimeout;

    if (!config?.positions?.nodes) {
      arrangeTimeout = setTimeout(() => {
        arrangeParentNodes(nodesRef.current, setNodes);
      }, 500);
    }

    return () => clearTimeout(arrangeTimeout);

    //eslint-disable-next-line
  }, []);

  const callRightCallback = (jobType) => {
    let rightFunction;

    if (jobType === "pre") {
      rightFunction = settingPreActivityData;
    } else if (jobType === "pre_submission") {
      rightFunction = settingPreSubmission;
    } else if (jobType === "post") {
      rightFunction = settingJobTriggerData;
    }

    return rightFunction;
  };

  const handleZoom = (direction = "in") => {
    const { x, y, zoom } = getViewport();

    const zoomFactor = 1.2;
    const newZoom = direction === "in" ? Math.min(zoom * zoomFactor, 2) : Math.max(zoom / zoomFactor, 0.1);

    const deltaZoom = Math.abs(newZoom - zoom);

    // Adjust offset to simulate bottom-right focus
    const offsetX = 100 * deltaZoom;
    const offsetY = 100 * deltaZoom;

    const newViewport = {
      x: direction === "in" ? x - offsetX : x + offsetX,
      y: direction === "in" ? y - offsetY : y + offsetY,
      zoom: newZoom
    };

    setViewport(newViewport, { duration: 300 });
  };

  const handleMove = (type) => {
    const { x, y, zoom } = getViewport();

    let yDimension;

    if (type === REACT_FLOW_TOOL_ACTION.MOVE_UP) {
      yDimension = y - 50;
    } else {
      yDimension = y + 50;
    }

    const newViewport = { x, y: yDimension, zoom };
    setViewport(newViewport, { duration: 300 });
  };

  const handleToolPanel = (action) => {
    switch (action) {
      case REACT_FLOW_TOOL_ACTION.ZOOM_IN:
        handleZoom();
        break;

      case REACT_FLOW_TOOL_ACTION.ZOOM_OUT:
        handleZoom("out");
        break;

      case REACT_FLOW_TOOL_ACTION.MOVE_UP:
        handleMove(REACT_FLOW_TOOL_ACTION.MOVE_UP);
        break;

      case REACT_FLOW_TOOL_ACTION.M0VE_DOWN:
        handleMove(REACT_FLOW_TOOL_ACTION.M0VE_DOWN);
        break;

      default:
        break;
    }
  };

  const onAddClick = (jobType) => {
    const jobKey = jobTriggerDataKey[jobType].jobKey;
    const jobs = allJobs[jobKey];
    setAdJob({ jobs, jobType });
  };

  const addStateChangeLogic = (IDKey, type) => {
    const configKey = type === "state" ? "state_change_logic" : "activity_change_logic";

    const array = [...logic[configKey], { [IDKey]: "", condition: "true" }];

    if (type === "state") {
      settingStateChangeLogic(array);
    } else {
      settingActivityChangeLogic(array);
    }
    setEditJob({ activity: array[array.length - 1], type, index: array.length - 1 });
  };

  const addJobHandler = (job, JobType) => {
    const configKey = jobTriggerDataKey[JobType].configKey;
    const activityConfigs = config[configKey];

    const allConfigs = [...activityConfigs, job];

    callRightCallback(JobType)(allConfigs);
    setAdJob(undefined);
  };

  const editJobCallBack = (jobData) => {
    setEditJob({ activity: jobData.activity, jobs: jobData.allJobs, type: jobData.parent, index: jobData.index });
  };

  const removeUnwantedEdge = (nodeId) => {
    const newEdges = edgesRef.current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
    setEdges(newEdges);
  };

  const deleteStateChangeLogic = (data, nodeId) => {
    const configKey = data.parent === "state" ? "state_change_logic" : "activity_change_logic";

    const array = [...logic[configKey]];
    const newArray = array.filter((_, i) => i !== data.index);

    settingStateChangeLogic(newArray);
    removeUnwantedEdge(nodeId);
  };

  const deleteJobCallback = (jobData, nodeId) => {
    const jobConfigKey = jobTriggerDataKey[jobData.parent].configKey;
    const jobConfigs = config[jobConfigKey];

    const newTriggerData = jobConfigs.filter((job) => job.id !== jobData.activity.id);

    callRightCallback(jobData.parent)(newTriggerData);
    removeUnwantedEdge(nodeId);
  };

  const sortTheConfig = (direction, { parent, index }) => {
    const configKey = jobTriggerDataKey[parent]?.configKey;
    const configList = config[configKey];

    if (!configList || index < 0 || index >= configList.length) return;

    const swapIndex = direction === "up" ? index - 1 : direction === "down" ? index + 1 : -1;
    if (swapIndex < 0 || swapIndex >= configList.length) return;

    [configList[index], configList[swapIndex]] = [configList[swapIndex], configList[index]];

    callRightCallback(parent)(configList);
  };

  //Call from GroupNode, after arrangement of children
  const updateParentPosition = () => {
    setTimeout(() => arrangeParentNodes(nodesRef.current, setNodes), 500);
  };

  const defaultParentNode = (nodeVisibility) =>
    getDefaultNodes(
      isAutoChildAlignMentRequired || config?.positions?.nodes,
      isAutoChildAlignMentRequired,
      onAddClick,
      updateParentPosition,
      addStateChangeLogic,
      nodeVisibility
    );

  const mapAllJobs = (
    config,
    node,
    setJobTriggerData,
    settingPreSubmission,
    stateId,
    actId,
    allJobs,
    jobsVisibility = toggleStates
  ) => {
    const nodes = [];
    const edges = [];

    const getRandomPosition = (baseY, index) => ({
      x: 20 + Math.floor(Math.random() * 100),
      y: (baseY || 0) + (index + 1) * 50
    });

    const getLogic = (jobType) => {
      const allLogic = logic[jobType.key];

      return jobType.jobType === "activity"
        ? allLogic?.filter((logicJob) => logicJob.activity_id)
        : allLogic?.filter((logicJob) => logicJob.state_id);
    };

    for (const eachJob of jobTypes) {
      //check visibility of the job
      if (!jobsVisibility[eachJob.jobType]) {
        continue;
      }

      const jobs = (["state", "activity"].includes(eachJob.jobType) ? getLogic(eachJob) : config[eachJob.key]) || [];

      if (!jobs.length > 0) continue;

      const basePosition = node[eachJob.key];
      const parentId = eachJob.parentId;
      let initialNodeId = null;

      if (eachJob.jobType === "post") {
        initialNodeId = `${parentId}.0`;

        nodes.push({
          id: initialNodeId,
          position: getPositionFromConfig(
            isAutoChildAlignMentRequired || config?.positions?.nodes,
            "post_function"
          ) || {
            x: 50,
            y: basePosition?.y || 0
          },
          data: { jobTriggerFormula: config["jobs_trigger_formula"], setJobTriggerFormula },
          parentId,
          type: "functionNode",
          extent: "parent"
        });
      }

      jobs.forEach((activity, index) => {
        const nodeId = `${parentId}.${index + 1}`;
        nodes.push({
          id: nodeId,
          position:
            getPositionFromConfig(
              isAutoChildAlignMentRequired || config?.positions?.nodes,
              `${eachJob.jobType}_${index}`
            ) || getRandomPosition(basePosition?.y, index),
          data: {
            activity,
            setFunction: eachJob.jobType === "pre" ? settingPreSubmission : setJobTriggerData,
            stateId,
            actId,
            templateObject,
            selectionObj: selectionObj[eachJob.jobType],
            index,
            currentTab,
            isReadOnly: true,
            allJobs: allJobs[eachJob.jobKey],
            isSmJob: false,
            parent: eachJob.jobType,
            isLastElement: index === jobs.length - 1,
            editJob: editJobCallBack,
            deleteJob: ["state", "activity"].includes(eachJob.jobType) ? deleteStateChangeLogic : deleteJobCallback,
            showMove: jobs.length > 1,
            moveNode: sortTheConfig
          },
          parentId,
          type: "customNode",
          extent: "parent"
        });

        if (eachJob.jobType === "post" && index === 0 && initialNodeId) {
          const firstNodeId = `${parentId}.1`;
          const edgeId = `e${initialNodeId}-${firstNodeId}`;
          const existingEdge = (config?.positions?.edges || []).find((edge) => edge.id === edgeId);

          if (existingEdge) {
            edges.push(existingEdge);
          } else {
            edges.push({
              id: `e${initialNodeId}-${firstNodeId}`,
              type: "step",
              source: initialNodeId,
              nodeType: "child",
              target: firstNodeId,
              sourceHandle: `${initialNodeId}-source-bottom`,
              targetHandle: `${firstNodeId}-target-top`,
              markerEnd: {
                type: "arrowclosed",
                width: 20,
                height: 20,
                color: "black"
              }
            });
          }
        }

        if (index > 0) {
          const prevNodeId = `${parentId}.${index}`;
          const edgeId = `e${prevNodeId}-${nodeId}`;
          const existingEdge = (config?.positions?.edges || []).find((edge) => edge.id === edgeId);

          if (existingEdge) {
            edges.push(existingEdge);
          } else {
            edges.push({
              id: edgeId,
              type: "step",
              source: prevNodeId,
              nodeType: "child",
              target: nodeId,
              sourceHandle: `${prevNodeId}-source-bottom`,
              targetHandle: `${nodeId}-target-top`,
              markerEnd: {
                type: "arrowclosed",
                width: 20,
                height: 20,
                color: "black"
              }
            });
          }
        }
      });
    }

    return { nodes, edges };
  };

  useEffect(() => {
    if (!config || Object.keys(allJobs).length === 0) return;

    setIsLoading(true);

    const { nodes: childNodes, edges: childEdges } = mapAllJobs(
      config,
      defaultNodePosition,
      settingPreActivityData,
      settingPreSubmission,
      stateid,
      actId,
      allJobs,
      undefined
    );

    const allNodes = [...defaultParentNode(toggleStates), ...childNodes];
    setNodes(allNodes);

    // Change edge type to 'bezier' if currentTab === "Job Node"
    const adjustedChildEdges = childEdges.map((edge) => {
      if (currentTab === "Job Node") {
        return { ...edge, type: "bezier" };
      }
      return edge;
    });

    if (config?.positions?.edges) {
      //THis is for old saved config, TODO: remove this later
      const nodeTypeAdded = adjustedChildEdges.map((childEdge) => {
        if (childEdge.nodeType) {
          return childEdge;
        } else {
          return { ...childEdge, nodeType: childEdge.id.includes(".") ? "child" : "groupNode" };
        }
      });

      const onlyChildren = nodeTypeAdded.filter((childEdge) => childEdge.nodeType === "child");
      const parentEdges = getDefaultParentEdges(allNodes, adjustedChildEdges);
      setEdges(() => [...onlyChildren, ...parentEdges]);
    } else {
      const parentEdges = getDefaultParentEdges(allNodes, edges || []);
      setEdges(() => [...parentEdges, ...adjustedChildEdges]);
    }

    setTimeout(() => {
      setIsLoading(false);
    }, 800);

    //eslint-disable-next-line
  }, [config, allJobs, currentTab]);

  const toggleNodeHandler = (job, state) => {
    setIsLoading(true);
    const newState = { ...toggleStates, [job]: state };
    setToggleStates(newState);

    const allVisibleParent = defaultParentNode(newState);
    const toggledNode = allVisibleParent.find((visibleNode) => visibleNode.data.type === job);

    const { nodes: childNodes, edges: childEdges } = mapAllJobs(
      config,
      defaultNodePosition,
      settingPreActivityData,
      settingPreSubmission,
      stateid,
      actId,
      allJobs,
      newState
    );

    let allNodes = [...allVisibleParent, ...childNodes];
    let allEdges = [...childEdges].filter((edge) => edge.nodeType === "child");

    allNodes = allNodes.filter((node) => node.data.type !== job);

    if (state === true) {
      allNodes = [toggledNode, ...allNodes];
    }

    const parentEdges = getDefaultParentEdges(allNodes, edges);
    allEdges = [...parentEdges, ...allEdges];

    setNodes(allNodes);
    setEdges(allEdges);

    setTimeout(() => {
      arrangeParentNodes(nodesRef.current, setNodes);
      setIsLoading(false);
    }, 500);
  };

  const editJobTriggerData = (newData, job) => {
    const jobCopy = structuredClone(job);

    const jobConfigKey = jobTriggerDataKey[jobCopy.type].configKey;
    const jobConfigs = config[jobConfigKey];

    const updatedJobConfigs = jobConfigs.map((job) => {
      if (job.id === newData.id) {
        return newData;
      }

      return job;
    });

    callRightCallback(jobCopy.type)(updatedJobConfigs);
    setEditJob(undefined);
  };

  const changeLogic = (key, value, index) => {
    const configkey = editJob.type === "state" ? "state_change_logic" : "activity_change_logic";
    const array = [...logic[configkey]];

    array[index][key] = value;
    if (editJob.type === "state") {
      settingStateChangeLogic(array);
    } else {
      settingActivityChangeLogic(array);
    }
  };

  const isValidConnection = useCallback((oldConnection, newConnection) => {
    // Only allow connections where source and target are the same node
    if (oldConnection.source === newConnection.source && oldConnection.target === newConnection.target) {
      return true;
    }

    return false;
  }, []);

  const onReconnect = useCallback((oldEdge, newConnection) => {
    if (!isValidConnection(oldEdge, newConnection)) {
      return; // Reject the reconnection
    }

    setEdges((els) => reconnectEdge(oldEdge, newConnection, els, { shouldReplaceId: false }));

    //eslint-disable-next-line
  }, []);

  const handleEdgeTypeChange = useCallback(
    (newType) => {
      setEdges((eds) => eds.map((e) => (e.id === selectedEdge?.id ? { ...e, type: newType } : e)));
      setSelectedEdge(null);
    },
    [selectedEdge, setEdges]
  );

  // Handle edge click
  const handleEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();

    // Get mouse position relative to the ReactFlow container
    const flowContainer = event.currentTarget.closest(".react-flow");
    const containerRect = flowContainer.getBoundingClientRect();

    setSelectorPosition({
      x: event.clientX - containerRect.left,
      y: event.clientY - containerRect.top,
      visible: true
    });

    setSelectedEdge(edge);
  }, []);

  // Close edge type selector when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectedEdge && !event.target.closest(".edge-type-selector")) {
        setSelectedEdge(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [selectedEdge]);

  return (
    <>
      {isLoading && (
        <div className="overlay-loader">
          <Loader />
        </div>
      )}
      <div className="job_flow">
        {currentTab !== "Job Node" && <ToggleJobs toggleStates={toggleStates} handleToggleChange={toggleNodeHandler} />}
        <div className="sm_blueprint_renderer_box diagramCanvasOutBox page_main_section p_0">
          <div className="d_flex position_absolute w_100 justify_end pr_5 pt_3">
            <ToolPanel callback={handleToolPanel} />
          </div>
          {currentTab !== "Job Node" && (
            <ToggleJobs toggleStates={toggleStates} handleToggleChange={toggleNodeHandler} />
          )}
          <ReactFlow
            panOnScroll
            panOnScrollMode="vertical"
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            onReconnect={onReconnect}
            fitView
            zoomOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            translateExtent={[
              [0, 0],
              [Infinity, Infinity]
            ]}
            minZoom={0.1}
            maxZoom={2}
            onEdgeClick={handleEdgeClick}
            onMove={() => setSelectedEdge(null)}
            onNodeDragStart={() => setSelectedEdge(null)}
          >
            <Background />
          </ReactFlow>
          {selectedEdge && selectorPosition.visible && (
            <div
              className="edge-type-selector"
              style={{
                position: "absolute",
                left: selectorPosition.x + 10,
                top: selectorPosition.y + 20,
                background: "white",
                padding: "8px",
                zIndex: 10,
                border: "1px solid #ddd",
                borderRadius: "4px",
                boxShadow: "8px 8px 24px 3px #D7D7D7"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <EdgeTypeRadio
                selected={selectedEdge?.type || "bezier"}
                onChange={(id) => {
                  handleEdgeTypeChange(id);
                  setSelectorPosition((prev) => ({ ...prev, visible: false }));
                }}
              />
            </div>
          )}
        </div>

        {editJob && ["state", "activity"].includes(editJob.type) ? (
          <BuilderRightSideBar
            isOpen={true}
            heading="State Change Logic"
            onClose={() => resetSm(editJob, logic, settingStateChangeLogic, settingActivityChangeLogic, setEditJob)}
          >
            <EditStateChangeLogic
              logic={editJob.activity}
              index={editJob.index}
              selectionObj={selectionObj[editJob.type] || {}}
              changeLogic={changeLogic}
              setIsOpen={editJob}
              type={editJob.type}
            />
          </BuilderRightSideBar>
        ) : (
          <BuilderRightSideBar
            style={{ width: "600px" }} //TODO:standardize it later
            isOpen={editJob ? true : false}
            heading="Edit Job"
            onClose={() => setEditJob(undefined)}
            size="medium"
          >
            <div className="edit_activity_job">
              <JobConfigration
                editJob={editJob?.activity}
                smId={stateid}
                actId={actId}
                onClick={(data) => editJobTriggerData(data, editJob)}
                templateObject={templateObject}
                allJobs={editJob?.jobs}
                isSmJob={isSmJob}
              />
            </div>
          </BuilderRightSideBar>
        )}

        {addJob && (
          <BuilderRightSideBar
            style={{ width: "600px" }} //TODO:standardize it later
            isOpen={addJob}
            heading="Add Job"
            onClose={() => setAdJob(undefined)}
            size="medium"
          >
            <div className="edit_activity_job">
              <JobConfigration
                smId={stateid}
                actId={actId}
                onClick={(newJob) => addJobHandler(newJob, addJob.jobType)}
                templateObject={templateObject}
                allJobs={addJob.jobs}
                isSmJob={isSmJob}
              />
            </div>
          </BuilderRightSideBar>
        )}
      </div>
    </>
  );
}

const resetSm = (editJob, logic, settingStateChangeLogic, settingActivityChangeLogic, setEditJob) => {
  const configKey = editJob.type === "state" ? "state_change_logic" : "activity_change_logic";
  const key = editJob.type === "state" ? "state_id" : "activity_id";

  const selectedLogic = logic[configKey];

  if (selectedLogic[editJob.index][key] === "") {
    if (editJob.type === "state") {
      settingStateChangeLogic(selectedLogic.filter((_, index) => index !== editJob.index));
    } else {
      settingActivityChangeLogic(selectedLogic.filter((_, index) => index !== editJob.index));
    }
  }

  setEditJob(undefined);
};

const arrangeParentNodes = (nodes, setNodes) => {
  let yOffset = 100;

  if (nodes) {
    const updatedNodes = nodes
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((node) => {
        if (node.measured?.height && ["groupNode", "circleNode"].includes(node.type)) {
          const updatedNode = {
            ...node,
            position: { x: node.type === "circleNode" ? 410 : 400, y: yOffset } // form circle radius less than group circle
          };

          yOffset += node.measured.height + 300;
          return updatedNode;
        }

        return node;
      });

    setNodes(updatedNodes);
  }
};
